import { WorkflowInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import type Graph from 'graphology';

import { getTaskToJsonErrors } from './getTaskToJsonErrors';
import { getSlotRangeValidationErrors } from './validateSlotRanges';
import { walkTaskListForValidation } from './walkTaskListForValidation';
import type {
  AllTaskProperties,
  GraphEdges,
  InstrumentForLocation,
} from '../../../types';
import { LabwareAndSourceTaskByTaskId } from '../../selectors/selectLabwareAndSourceNodeByTaskId';
import type { LabwareByHandleIndexByTaskId } from '../../selectors/types';
import type {
  DataDependenciesByTaskId,
  JsonLabwareById,
  MaybeWorkflowSerializationErrors,
  TimeEstimateValuesByTaskId,
  WorkflowSerializationError,
} from '../../shared/types';
import { DriverActionConfigsByActionNameByVersionedDriverName } from '../../workflow/types';
import { TaskDependenciesByTaskId } from '../getJsonTaskDependencies';
import { err } from '../utils';

function collectErrors<T extends unknown[]>(
  ...errors: { [K in keyof T]: MaybeWorkflowSerializationErrors<T[K]> }
): WorkflowSerializationError[] {
  return errors.reduce<WorkflowSerializationError[]>((acc, maybeError) => {
    if (maybeError.isError) acc.push(...maybeError.errors);
    return acc;
  }, []);
}

export default function tasksToJson(
  taskIds: string[],
  labwareById: MaybeWorkflowSerializationErrors<JsonLabwareById>,
  labwareIdsForTaskByTaskId: LabwareByHandleIndexByTaskId,
  fieldErrors: WorkflowSerializationError[],
  taskPropertiesById: Record<string, AllTaskProperties>,
  graphHasCycles: boolean,
  graph: Graph,
  timeEstimateValuesByTaskId: TimeEstimateValuesByTaskId,
  taskDependenciesByTaskId: MaybeWorkflowSerializationErrors<TaskDependenciesByTaskId>,
  dataDependenciesByTaskId: DataDependenciesByTaskId,
  instrumentsById: Record<string, InstrumentForLocation>,
  driverActionConfigs: DriverActionConfigsByActionNameByVersionedDriverName,
  labwareAndSourceTaskByTaskId: LabwareAndSourceTaskByTaskId,
  labwareDependencyEdges: GraphEdges
): MaybeWorkflowSerializationErrors<WorkflowInput['tasks']> {
  const errors = [
    ...getTaskToJsonErrors({
      graphHasCycles,
      labwareIdsForTaskByTaskId,
      taskIds,
      taskPropertiesById,
      timeEstimateValuesByTaskId,
    }),
    ...fieldErrors,
    ...collectErrors(taskDependenciesByTaskId, labwareById),
    ...getSlotRangeValidationErrors({
      taskIds,
      taskPropertiesById,
      instrumentsById,
      labwareAndSourceTaskByTaskId,
    }),
  ];

  if (taskDependenciesByTaskId.isError) {
    return err(errors);
  }

  const tasks = walkTaskListForValidation(
    taskIds,
    graph,
    labwareIdsForTaskByTaskId,
    taskPropertiesById,
    timeEstimateValuesByTaskId,
    taskDependenciesByTaskId.data,
    dataDependenciesByTaskId,
    instrumentsById,
    driverActionConfigs,
    labwareAndSourceTaskByTaskId,
    labwareDependencyEdges
  );

  if (tasks.isError) {
    errors.push(...tasks.errors);
  }

  if (errors.length > 0) {
    return err(errors);
  }

  return tasks;
}
